import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { get } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, Fragment, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useRecoilValue } from 'recoil';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Divider from '~/components/Divider';
import Icon from '~/components/Icon';
import OrderProductItem from '~/components/Items/OrderProductItem';
import CancelOrderModal from '~/components/Modals/CancelOrderModal';
import ReviewModal from '~/components/Modals/ReviewModal';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { PATH } from '~/constants/enum';
import { ORDER_STATUS } from '~/constants/order';
import { GET_CUSTOMER_ORDER_DETAIL_GQL } from '~/data/user';
import { OrderDetailResponse, UseOrderDetailResponse } from '~/data/user/interfaces';

import { OrderStatus } from '~/services/order';
import { UserAtom } from '~/services/user';
import styles from '~/styles/pages/OrderDetails.module.scss';
import { formatOrderStatusFlows } from '~/utils/format';
import { formatNumber } from '~/utils/number';
import Star from '~/components/Star';

interface OrderDetailsProps {
    data: OrderDetailResponse;
    loading?: boolean;
    onChangeStart?: (start: number) => void;
    start?: number;
}

const OrderInformation: FC<OrderDetailsProps> = ({ data, onChangeStart, start }) => {
    const { status, number } = data;
    const orderStatusFlows = formatOrderStatusFlows(status) ?? [];
    const [showOrderStatus, setShowOrderStatus] = useState(false);
    const currentUser = useRecoilValue(UserAtom.currentUser);

    const orderStatus = ORDER_STATUS[status] ?? {
        icon: '',
        name: status,
        color: Colors.GREY_400
    };
    return (
        <div className={styles.order_information}>
            <div className="flex gap-3 md:items-center">
                <Link href="/profile/order-history" className="block md:hidden h-fit">
                    <Icon name="arrow-square-left-2" color={Colors.GREY_900} size={32} />
                </Link>
                <div className="flex md:items-center justify-between gap-x-3 gap-y-1 flex-1 flex-col md:flex-row">
                    <div className="flex items-center gap-2">
                        <Text type="title-1" color={Colors.GREY_900} className={styles.order_information_title}>
                            Order Information
                        </Text>
                    </div>

                    <div className="flex items-center gap-2">
                        <Text type="body-2" color={Colors.GREY_300} className={styles.order_id}>
                            Order ID:
                        </Text>
                        <Link
                            href={
                                process.env.NEXT_PUBLIC_URL +
                                '/tracking-order?id=' +
                                number +
                                '&email=' +
                                currentUser?.email
                            }
                            target="_blank"
                        >
                            <Text
                                type="body-2"
                                className={classNames(styles.order_id, 'hover:underline')}
                                color={Colors.ACCENT_BLUE_50}
                            >
                                {number}
                            </Text>
                        </Link>
                    </div>
                </div>
            </div>

            <Divider
                dividerClassName="border-t-[6px] md:border-t-8 -mx-4 md:-mx-7 md:hidden"
                dividerColor={Colors.GREY_50}
            />

            {status === OrderStatus.DELIVERED && (
                <Fragment>
                    <div className="flex flex-col items-center justify-center gap-2 md:py-4">
                        <Text
                            type="label-2"
                            color={Colors.ACCENT_GREEN_50}
                            className="text-center !text-base md:!text-xl"
                        >
                            Congratulation! Delivery Successfully
                        </Text>
                        <Text type="body-3" color={Colors.GREY_700} className="inline !text-xs md:!text-sm">
                            Let us know what you think of our products
                        </Text>
                        <div className="flex gap-1">
                            <Star isEdit onChange={(v: any) => onChangeStart?.(v)} value={start} />
                        </div>
                    </div>

                    <Divider
                        dividerClassName="border-t-[6px] md:border-t-8 -mx-4 md:-mx-7"
                        dividerColor={Colors.GREY_50}
                    />
                </Fragment>
            )}

            {status === OrderStatus.CANCELLED && (
                <Fragment>
                    <div className="flex flex-col items-center justify-center gap-2 md:py-4">
                        <Text type="label-2" color={Colors.ACCENT_RED_50} className="!text-base md:!text-xl">
                            Refunded!
                        </Text>
                        <div className="text-center">
                            <Text
                                type="body-3"
                                color={Colors.GREY_700}
                                className="inline text-center !text-xs md:!text-sm"
                            >
                                Refund requests are accepted. The amount will be refunded to your credit card within{' '}
                            </Text>
                            <Text type="label-4" color={Colors.PRIMARY} className="inline">
                                24 hours
                            </Text>
                        </div>
                    </div>

                    <Divider
                        dividerClassName="border-t-[6px] md:border-t-8 -mx-4 md:-mx-7"
                        dividerColor={Colors.GREY_50}
                    />
                </Fragment>
            )}

            <div
                className={classNames(styles.flow_status, {
                    'max-w-[400px] mx-auto w-full': data.status === OrderStatus.CANCELLED
                })}
            >
                {orderStatusFlows.map((item, index) => {
                    const nextFlow = orderStatusFlows?.[index + 1];

                    return (
                        <Fragment key={item.name}>
                            <div
                                className={classNames(styles.item, {
                                    [styles.active]: item.active,
                                    [styles.danger]: item.danger
                                })}
                            >
                                <div className={styles.icon_wrapper}>
                                    <Icon name={item.icon} color={Colors.GREY_00} className={styles.icon} />
                                </div>
                                <Text className={styles.name} type="label-3">
                                    {item.name}
                                </Text>
                            </div>
                            {index !== orderStatusFlows.length - 1 && (
                                <Divider
                                    dividerClassName="border-t w-full mt-4 md:mt-6"
                                    dividerColor={
                                        nextFlow?.active || nextFlow?.danger ? Colors.PRIMARY : Colors.GREY_100
                                    }
                                />
                            )}
                        </Fragment>
                    );
                })}
            </div>

            {/* {status && status !== OrderStatus.ORDER_PLACED && (
        <div className="flex md:items-center gap-y-1 gap-x-2 flex-col md:flex-row">
          <Text
            type="label-3"
            color={Colors.GREY_800}
            className="!text-sm md:!text-base"
          >
            Tracking Number:
          </Text>
          <Link href={number} target="_blank">
            <Text
              type="body-2"
              color={Colors.ACCENT_BLUE_50}
              className="underline !text-sm md:!text-base"
            >
              {number}
            </Text>
          </Link>
        </div>
      )} */}

            {/* {refund_reason && status === OrderStatus.RETURN_REFUND && (
        <div className="flex md:items-center gap-y-1 gap-x-3 justify-between flex-col md:flex-row">
          <Text
            type="label-3"
            color={Colors.GREY_900}
            className="!text-sm md:!text-base"
          >
            Return/refund reason
          </Text>
          <Text
            type="body-3"
            color={Colors.GREY_300}
            className="!text-sm md:!text-base"
          >
            {data.refund_reason}
          </Text>
        </div>
      )} */}

            {/* <div className={styles.order_status}>
        <div
          className="flex justify-between items-center md:items-start md:justify-normal md:max-w-[175px] md:w-full cursor-pointer md:cursor-auto"
          onClick={() => setShowOrderStatus(!showOrderStatus)}
        >
          <Text type="title-4" color={Colors.GREY_900}>
            Order status
          </Text>
          <Icon
            name="arrow-square-down-2"
            className={classNames("block md:hidden", {
              "rotate-180": showOrderStatus,
            })}
          />
        </div>

        <Timeline
          className={classNames(styles.timeline, {
            [styles.open]: showOrderStatus,
          })}
          items={data?.order_status_logs?.map((log: any) => ({
            label: format(log.created_at, "HH:mm MMM dd, yyyy"),
            content: log.name,
          }))}
        />
      </div> */}

            {/* {data.shipping_unit && data.status !== OrderStatus.ORDER_PLACED && (
        <div className={styles.shipment_unit}>
          <div className="flex-1 flex gap-2 md:gap-5">
            <Image
              src={data.shipping_unit?.logo!}
              alt={data.shipping_unit?.name!}
              width={48}
              height={48}
              priority={false}
            />

            <div className="flex flex-col gap-1">
              <Text
                type="title-3"
                color={Colors.GREY_900}
                className={styles.name}
              >
                {data.shipping_unit?.name}
              </Text>
              <div className="flex items-center gap-2">
                <Text
                  type="body-2"
                  color={Colors.GREY_300}
                  className={styles.id}
                >
                  Bill of lading ID:
                </Text>
                <Text
                  type="body-2"
                  color={Colors.HEADER_BG}
                  className={styles.id}
                >
                  {data.shipping_unit?.order_id}
                </Text>
              </div>
            </div>
          </div>

          <Text type="body-3" color={Colors.GREY_700}>
            Expected delivery on{" "}
            {format(
              new Date(data.shipping_unit?.expected_delivery),
              "MMM dd, yyyy"
            )}
          </Text>
        </div>
      )} */}
        </div>
    );
};

const ShippingInformation: FC<OrderDetailsProps> = ({ data, loading }) => {
    const { shipping_address } = data;
    return (
        <div className={styles.shipping_information}>
            <Text type="title-3" color={Colors.GREY_900}>
                Shipping Information
            </Text>

            <div className="flex gap-4">
                <div className={classNames(styles.shipping_item, 'flex-1')}>
                    <Text type="label-4" color={Colors.GREY_900}>
                        Shipping Address
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {shipping_address?.firstname} {shipping_address?.lastname}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {[
                            shipping_address?.street,
                            shipping_address?.street1,
                            shipping_address?.city,
                            shipping_address?.postcode
                        ]
                            ?.filter((item: any) => !!item)
                            .join(', ')}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {[shipping_address?.country_code, shipping_address?.region]
                            ?.filter((item: any) => !!item)
                            .join(', ')}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        T: {shipping_address?.telephone}
                    </Text>
                </div>
                <div className={classNames(styles.shipping_item, 'flex-1')}>
                    <Text type="label-4" color={Colors.GREY_900}>
                        Billing Address
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {shipping_address?.firstname} {shipping_address?.lastname}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {[
                            shipping_address?.street,
                            shipping_address?.street1,
                            shipping_address?.city,
                            shipping_address?.postcode
                        ]
                            ?.filter((item: any) => !!item)
                            .join(', ')}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        {[shipping_address?.country_code, shipping_address?.region]
                            ?.filter((item: any) => !!item)
                            .join(', ')}
                    </Text>
                    <Text className="text-grey-500" type="body-2">
                        T: {shipping_address?.telephone}
                    </Text>
                </div>
            </div>
        </div>
    );
};

const OrderDetail: FC<OrderDetailsProps> = ({ data, loading }) => {
    const { subtotal, shipping_handling, discounts, grand_total } = data?.total || {};

    const discount = discounts?.reduce((total: number, item) => total + item.amount.value, 0) || 0;
    return (
        <div className={styles.order_detail}>
            <Text type="title-3" color={Colors.GREY_900}>
                Order detail {!!data?.items?.length && `(${data?.items?.length})`}
            </Text>

            <div className={styles.order_items}>
                {loading && (
                    <div className="flex gap-4">
                        <Skeleton className="aspect-square h-[70px]"></Skeleton>
                        <div className="flex-1">
                            <Skeleton className="w-full"></Skeleton>
                            <Skeleton className="w-1/2"></Skeleton>
                            <Skeleton className="w-[100px]"></Skeleton>
                        </div>
                    </div>
                )}
                {!loading &&
                    data?.items?.map((orderItem, index: number) => (
                        <Fragment key={orderItem?.product_sku}>
                            <OrderProductItem orderItem={orderItem} className="py-2 md:p-4" />
                            {index !== data?.items?.length - 1 && <Divider dividerType="dashed" />}
                        </Fragment>
                    ))}
            </div>

            <Divider
                dividerClassName="border-t-[6px] md:border-t-8 -mx-4 md:-mx-7 md:hidden"
                dividerColor={Colors.GREY_50}
            />

            <div className={styles.payment_information}>
                <div className={styles.item}>
                    <Text type="body-2" color={Colors.GREY_800} className={styles.label}>
                        Subtotal
                    </Text>
                    {loading ? (
                        <Skeleton width={100} />
                    ) : (
                        <Text type="body-2" color={Colors.GREY_800} className={styles.value}>
                            ${subtotal?.value?.toFixed(2)}
                        </Text>
                    )}
                </div>
                <div className={styles.item}>
                    <Text type="body-2" color={Colors.GREY_800} className={styles.label}>
                        Transport fee
                    </Text>
                    {loading ? (
                        <Skeleton width={100} />
                    ) : (
                        <Text type="body-2" color={Colors.GREY_800} className={styles.value}>
                            ${shipping_handling?.total_amount?.value?.toFixed(2)}
                        </Text>
                    )}
                </div>
                <div className={styles.item}>
                    <Text type="body-2" color={Colors.GREY_800} className={styles.label}>
                        Discount voucher
                    </Text>
                    {loading ? (
                        <Skeleton width={100} />
                    ) : (
                        <Text type="body-2" color={Colors.ACCENT_RED_50} className={styles.value}>
                            -${formatNumber(discount, 2)}
                        </Text>
                    )}
                </div>
                <Divider dividerType="dashed" />
                <div className={styles.item}>
                    <Text type="body-2" color={Colors.GREY_800} className={styles.label}>
                        Total payment
                    </Text>
                    {loading ? (
                        <Skeleton width={100} />
                    ) : (
                        <Text type="title-3" color={Colors.PRIMARY} className={styles.total_payment}>
                            ${grand_total?.value?.toFixed(2)}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};

const PaymentMethod: FC<OrderDetailsProps> = ({ data, loading }) => {
    const { payment_methods } = data;
    // const cardNumber = data?.payment_method?.number_account?.toString();
    // const lastFourDigits = cardNumber?.slice(-4);

    return (
        <div className={styles.payment_method}>
            <div className="flex md:items-center justify-between gap-3 flex-col md:flex-row">
                <Text type="title-3" color={Colors.GREY_900}>
                    Payment method
                </Text>

                {/* <div className={styles.card}>
                    <Text type="label-5" color={Colors.GREY_300}>
                        {payment_methods?.[0]?.name}
                    </Text>
                    <Icon name="card" color={Colors.GREY_300} size={20} />
                </div> */}
            </div>

            <div className={styles.payment_item}>
                {loading ? (
                    <Skeleton width={100} />
                ) : (
                    <Text type="body-3" color={Colors.GREY_700}>
                        {payment_methods?.[0]?.name}
                    </Text>
                )}
                {/* <div className="flex items-center gap-3">
          <Image
            src="/svgs/logo_mastercard.svg"
            width={32}
            height={24}
            alt="master card"
            priority={false}
          />
          <Text type="body-3" color={Colors.GREY_800}>
            **** **** **** {lastFourDigits}
          </Text>
        </div> */}
            </div>
        </div>
    );
};

const OrderDetails: FC<{ slug: string }> = ({ slug }) => {
    const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [defaultStart, setDefaultStart] = useState(0);
    const router = useRouter();

    const { data: orderData, loading } = useQuery<UseOrderDetailResponse>(GET_CUSTOMER_ORDER_DETAIL_GQL, {
        variables: {
            param: slug
        },
        skip: !slug
    });

    const data: OrderDetailResponse = useMemo(() => {
        return get(orderData, 'customer.orders.items.[0]', {}) as OrderDetailResponse;
    }, [orderData]);

    // if (!!loading) {
    //     return <Skeleton />;
    // }

    const OrderAction = () => {
        switch (data.status) {
            case OrderStatus.WAITING_PAYMENT:
            case OrderStatus.PENDING_PAYMENT:
            case OrderStatus.PROCESSING: {
                return (
                    <div className={styles.order_action}>
                        {/* <Button
                            type="outline"
                            variant="secondary"
                            className="flex-1 min-h-10"
                            onClick={() => setShowCancelOrderModal(true)}
                        >
                            Cancel Order
                        </Button> */}
                        <Button onClick={() => router.push(PATH.ContactUs)} className="flex-1 min-h-10">
                            Contact Us
                        </Button>
                    </div>
                );
            }
            case OrderStatus.IN_TRANSIT: {
                return (
                    <Button className="flex-1 min-h-10" onClick={() => router.push(PATH.ContactUs)}>
                        Contact Us
                    </Button>
                );
            }
            // case OrderStatus.PROCESSING:
            case OrderStatus.DELIVERED: {
                return (
                    <div className={styles.order_action}>
                        <Button
                            type="outline"
                            variant="secondary"
                            className="flex-1 min-h-10"
                            onClick={() => setShowReviewModal(true)}
                        >
                            Write Review
                        </Button>
                        <Button className="flex-1 min-h-10" onClick={() => router.push(PATH.ContactUs)}>
                            Contact Us
                        </Button>
                    </div>
                );
            }
            // case OrderStatus.CANCELLED: {
            // return (
            //   <Button
            //     className="flex-1 min-h-10"
            //     onClick={() => {
            //       setCart((prev) => ({
            //         ...prev,
            //         items: prev.items?.concat(
            //           data.order_items.map((i: any) => ({
            //             id: v4(),
            //             product: i.product,
            //             selectedVariant: i.variant,
            //             quantity: i.quantity,
            //           }))
            //         ),
            //       }));
            //       router.push("/carts");
            //     }}
            //   >
            //     Buy Again
            //   </Button>
            // );
            // }
        }
    };
    const handleChangeStartDefault = (start: number) => {
        setDefaultStart(start);
        setShowReviewModal(true);
    };
    return (
        <Fragment>
            <Container rootClassName={styles.order_details} className={styles.wrapper}>
                <OrderInformation data={data} onChangeStart={handleChangeStartDefault} />
                <Divider dividerClassName="border-t-[6px] md:border-t-8" dividerColor={Colors.GREY_50} />
                <ShippingInformation data={data} loading={loading} />
                <Divider dividerClassName="border-t-[6px] md:border-t-8" dividerColor={Colors.GREY_50} />
                <OrderDetail data={data} loading={loading} />
                <Divider dividerClassName="border-t-[6px] md:border-t-8" dividerColor={Colors.GREY_50} />
                <PaymentMethod data={data} loading={loading} />
                <Divider dividerClassName="border-t-[6px] md:border-t-8" dividerColor={Colors.GREY_50} />
                <OrderAction />
            </Container>
            <CancelOrderModal open={showCancelOrderModal} onClose={() => setShowCancelOrderModal(false)} />
            <ReviewModal
                open={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                order_id={data?.number}
                items={data?.items || []}
                defaultStart={defaultStart}
            />
        </Fragment>
    );
};

export default OrderDetails;
